import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { ADMIN_TEST_ID, clearRumUser, identifyRumUser } from '../shared/utils/rum-user.util';

interface CognitoInitiateAuthResponse {
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: {
    USERNAME?: string;
    email?: string;
  };
  AuthenticationResult?: {
    IdToken: string;
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
}

interface CognitoAuthResponse {
  AuthenticationResult?: {
    IdToken: string;
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
  ChallengeName?: string;
  Session?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'cognito_id_token';
  private readonly EMAIL_KEY = 'user_email';
  private readonly TEST_ID_KEY = 'test_id';

  constructor(private http: HttpClient, private config: ConfigService) {}

  private get COGNITO_URL() {
    return `https://cognito-idp.${this.config.region}.amazonaws.com/`;
  }

  authenticateWithMagicLink(email: string, token: string, testId: string): Observable<boolean> {
    return this.initiateCustomAuth(email).pipe(
      switchMap(initResponse => {
        if (initResponse.AuthenticationResult?.IdToken) {
          this.setAuthData(initResponse.AuthenticationResult.IdToken, email, testId);
          return new Observable<boolean>(observer => {
            observer.next(true);
            observer.complete();
          });
        }
        
        if (initResponse.ChallengeName === 'CUSTOM_CHALLENGE' && initResponse.Session) {
          return this.respondToAuthChallenge(email, token, initResponse.Session).pipe(
            map(response => {
              if (response.AuthenticationResult?.IdToken) {
                this.setAuthData(response.AuthenticationResult.IdToken, email, testId);
                return true;
              }
              return false;
            })
          );
        }
        
        return new Observable<boolean>(observer => {
          observer.next(false);
          observer.complete();
        });
      })
    );
  }

  private initiateCustomAuth(email: string): Observable<CognitoInitiateAuthResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
    });

    const body = {
      AuthFlow: 'CUSTOM_AUTH',
      ClientId: this.config.clientId,
      AuthParameters: {
        USERNAME: email
      }
    };

    return this.http.post<CognitoInitiateAuthResponse>(this.COGNITO_URL, body, { headers });
  }

  private respondToAuthChallenge(email: string, token: string, session: string): Observable<CognitoAuthResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
    });

    const body = {
      ChallengeName: 'CUSTOM_CHALLENGE',
      ClientId: this.config.clientId,
      ChallengeResponses: {
        USERNAME: email,
        ANSWER: token
      },
      Session: session
    };

    return this.http.post<CognitoAuthResponse>(this.COGNITO_URL, body, { headers });
  }

  setAuthData(idToken: string, email: string, testId: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, idToken);
    sessionStorage.setItem(this.EMAIL_KEY, email);
    sessionStorage.setItem(this.TEST_ID_KEY, testId);
    // El login de admin se enriquece luego en AdminAuthService.getMe() con
    // nombre y rol; aquí solo se asegura que la sesión deje de ser anónima.
    if (testId !== ADMIN_TEST_ID) {
      identifyRumUser({ email, userType: 'candidate', testId });
    } else {
      identifyRumUser({ email, userType: 'admin' });
    }
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  getEmail(): string | null {
    return sessionStorage.getItem(this.EMAIL_KEY);
  }

  getTestId(): string | null {
    return sessionStorage.getItem(this.TEST_ID_KEY);
  }

  clearAuthData(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.EMAIL_KEY);
    sessionStorage.removeItem(this.TEST_ID_KEY);
    clearRumUser();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const exp = this.getTokenExpiration(token);
    if (exp === null) return true;
    return Date.now() < exp * 1000;
  }

  private getTokenExpiration(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof decoded.exp === 'number' ? decoded.exp : null;
    } catch {
      return null;
    }
  }
}

